/**
 * THE REUSABLE CONNECTOR CONFIGURATION FORM.
 *
 * One component renders every connector type's settings, driven by the field definitions the
 * API sends from the shared registry (packages/shared/src/connectors.ts). This is the thing that
 * stops the codebase growing a bespoke settings screen per integration — the pattern the
 * hard-coded Gmail/Outlook/Webhook/inbound-mail cards in the old ConnectivityModal were heading
 * towards.
 *
 * Because the SERVER validates against the same definitions, a field this form marks required is
 * a field the API refuses to switch on without — they cannot drift.
 *
 * SECRETS. The browser is never sent a stored credential. A secret field whose key appears in
 * `secretsSet` renders as "saved" with an empty input: typing replaces it, leaving it alone keeps
 * it. That is what makes renaming a connector safe — no password has to be retyped to do it.
 */
import { useState, useMemo } from "react";
import type { ReactNode } from "react";
import { Info, Mail, FileText, UploadCloud, Copy, Check, Sparkles, Briefcase, Search } from "./icons.js";
import { jobEmailAddress } from "@talenthop/shared";
import { getRequisitionStatus } from "../personas/users/requisitionFormat.js";

export interface RequisitionBrief {
  id: string;
  title: string;
  jobCode?: string | null;
  job_code?: string | null;
  state?: string;
}

export interface ConnectorFieldDef {
  key: string;
  label: string;
  type: "text" | "password" | "email" | "url" | "number" | "boolean" | "select" | "textarea";
  required?: boolean;
  secret?: boolean;
  help?: string;
  placeholder?: string;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  maxLength?: number;
  default?: string | number | boolean;
}

export type ConnectorFormValue = string | number | boolean | null;
export type ConnectorFormValues = Record<string, ConnectorFormValue>;

/** Seed a form from a type's defaults, then overlay whatever is already stored. */
export function initialValues(
  fields: readonly ConnectorFieldDef[],
  stored: ConnectorFormValues = {},
): ConnectorFormValues {
  const out: ConnectorFormValues = {};
  for (const f of fields) {
    const existing = stored[f.key];
    if (existing !== undefined && existing !== null) out[f.key] = existing;
    else if (f.default !== undefined) out[f.key] = f.default;
    else out[f.key] = f.type === "boolean" ? false : "";
  }
  return out;
}

/**
 * Client-side mirror of validateConnectorConfig, for the submit button's enabled state only.
 * The SERVER's verdict is authoritative — this exists so the button does not invite a click
 * that is going to 400, not to be the check that matters.
 */
export function missingRequired(
  fields: readonly ConnectorFieldDef[],
  values: ConnectorFormValues,
  secretsSet: readonly string[] = [],
): string[] {
  return fields
    .filter((f) => {
      if (!f.required) return false;
      // A credential already on file satisfies its own requirement.
      if (f.secret && secretsSet.includes(f.key)) return false;
      const v = values[f.key];
      return v === undefined || v === null || (typeof v === "string" && v.trim() === "");
    })
    .map((f) => f.label);
}

const labelStyle = { display: "flex", alignItems: "center", gap: 4, fontWeight: 600, fontSize: 13, marginBottom: 6 } as const;
const helpStyle = { fontSize: 12, color: "var(--ink-mute)", marginTop: 6, lineHeight: 1.45 } as const;

/** Render every field of one connector type. */
export function ConnectorFields({
  typeKey,
  fields,
  values,
  secretsSet = [],
  requisitions = [],
  onChange,
  disabled,
}: {
  typeKey?: string;
  fields: readonly ConnectorFieldDef[];
  values: ConnectorFormValues;
  /** Credential keys that already hold a value on the server. Values are never sent here. */
  secretsSet?: readonly string[];
  requisitions?: readonly RequisitionBrief[];
  onChange: (key: string, value: ConnectorFormValue) => void;
  disabled?: boolean;
}): ReactNode {
  const [copied, setCopied] = useState(false);
  const [selectedReqId, setSelectedReqId] = useState<string>("");
  const [searchReq, setSearchReq] = useState<string>("");

  const activeRequisitions = useMemo(() => {
    return requisitions.filter((r) => getRequisitionStatus(r.state) === "Active");
  }, [requisitions]);

  const filteredRequisitions = useMemo(() => {
    if (!searchReq.trim()) return activeRequisitions;
    const term = searchReq.toLowerCase().trim();
    return activeRequisitions.filter((r) => {
      const title = (r.title || "").toLowerCase();
      const code = (r.job_code || r.jobCode || "").toLowerCase();
      return title.includes(term) || code.includes(term);
    });
  }, [activeRequisitions, searchReq]);

  const activeRequisition = useMemo(() => {
    if (selectedReqId) {
      const found = activeRequisitions.find((r) => r.id === selectedReqId);
      if (found) return found;
    }
    return filteredRequisitions[0] ?? activeRequisitions[0] ?? null;
  }, [activeRequisitions, filteredRequisitions, selectedReqId]);

  // Only ever the real job code. Falling back to the requisition id produced an address the
  // ingestion can never route (it accepts 6-char codes only), so mail sent to it was lost.
  const jobCode = activeRequisition?.job_code || activeRequisition?.jobCode || null;
  const assignedEmail = jobCode ? jobEmailAddress(jobCode) : null;

  const handleCopyEmail = () => {
    if (!assignedEmail) return;
    navigator.clipboard
      .writeText(assignedEmail)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  const isEmailReceive = typeKey === "email_receive";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* ── EMAIL RECEIVING: Prominent Unique Address & 3-Step Process ────── */}
      {isEmailReceive && (
        <>
          {/* Unique Email Address Box */}
          <div className="t1s-cf-box">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="t1s-cf-eyebrow">Requirement receiving address</span>
                <span className="pill n">{activeRequisitions.length} active</span>
              </div>

              {activeRequisitions.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  {activeRequisitions.length > 3 && (
                    <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                      <Search
                        style={{
                          position: "absolute",
                          left: 10,
                          width: 13,
                          height: 13,
                          color: "var(--ink-mute)",
                          pointerEvents: "none",
                        }}
                      />
                      <input
                        type="text"
                        className="input sm"
                        aria-label="Search active jobs"
                        placeholder="Search active jobs…"
                        value={searchReq}
                        onChange={(e) => setSearchReq(e.target.value)}
                        style={{ paddingLeft: 30, paddingRight: 8, height: 34, fontSize: 12.5, width: 170 }}
                      />
                    </div>
                  )}

                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Briefcase style={{ width: 14, height: 14, color: "var(--ink-mute)" }} />
                    <select
                      className="input sm"
                      aria-label="Requirement"
                      style={{ fontSize: 12.5, padding: "2px 28px 2px 10px", height: 34, maxWidth: 240 }}
                      value={activeRequisition?.id ?? ""}
                      onChange={(e) => setSelectedReqId(e.target.value)}
                    >
                      {filteredRequisitions.length === 0 ? (
                        <option value="" disabled>
                          No matching active requirement
                        </option>
                      ) : (
                        filteredRequisitions.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.title}{r.job_code || r.jobCode ? ` (${r.job_code || r.jobCode})` : ""}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {activeRequisitions.length === 0 ? (
              <div className="t1s-cf-empty">
                No active requirements found. Only active requirements have email receiving addresses. Create or activate a requirement to start receiving candidate emails.
              </div>
            ) : !assignedEmail ? (
              <div className="t1s-cf-empty">
                Couldn’t load this requirement’s receiving address. Open the requirement page to see it.
              </div>
            ) : (
              <div className="t1s-cf-addr">
                <code>{assignedEmail}</code>
                <button
                  type="button"
                  className={`btn sm t1s-copy${copied ? " copied" : ""}`}
                  onClick={handleCopyEmail}
                  aria-label={copied ? "Copied" : "Copy assigned email address"}
                  data-tip={copied ? "Copied" : "Copy address"}
                  data-tip-pos="left"
                >
                  {copied ? <Check /> : <Copy />}
                  <span aria-live="polite">{copied ? "Copied" : "Copy"}</span>
                </button>
              </div>
            )}

            <p className="t1s-cf-help" style={{ marginTop: 8, marginBottom: 0 }}>
              This email address is assigned to this requirement. Forward candidate application emails to this address to
              automatically process resumes for this job.
            </p>
          </div>

          {/* 3-Step Visual Process Section */}
          <div className="t1s-cf-box">
            <div className="t1s-cf-eyebrow" style={{ marginBottom: 14 }}>How it works</div>

            {/* Step 1: Email Received */}
            <div className="t1s-step">
              <div className="t1s-step-n">1</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="t1s-step-t"><Mail /> Email received</div>
                <div className="t1s-step-d">Candidate application email is received and linked to this requirement.</div>
              </div>
            </div>

            <div className="t1s-step-link"><i /></div>

            {/* Step 2: AI Resume Scanning */}
            <div className="t1s-step">
              <div className="t1s-step-n">2</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="t1s-step-t"><FileText /> AI resume scanning</div>
                <div className="t1s-step-d">
                  If a resume is attached, TalentHop automatically extracts and scans the resume.
                </div>
                <div className="pill t" style={{ marginTop: 6 }}>
                  <Sparkles style={{ width: 12, height: 12 }} />
                  1 resume from your plan will be used.
                </div>
              </div>
            </div>

            <div className="t1s-step-link"><i /></div>

            {/* Step 3: Resume Missing */}
            <div className="t1s-step">
              <div className={`t1s-step-n${values.requestMissingResume !== false ? "" : " off"}`}>3</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="t1s-step-t">
                  <UploadCloud style={values.requestMissingResume !== false ? undefined : { color: "var(--ink-mute)" }} />
                  Resume missing
                  {values.requestMissingResume === false && <span className="pill n">Disabled</span>}
                </div>
                <div className="t1s-step-d">
                  {values.requestMissingResume !== false ? (
                    <>
                      If no resume is attached, TalentHop automatically sends the candidate an email requesting their resume.
                      <div style={{ marginTop: 4 }}>
                        Once the resume is received, it is added to the candidate and processed automatically.
                      </div>
                    </>
                  ) : (
                    "Automatic resume request is disabled. Candidates without an attached resume will be filed with pending resume status."
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {fields.map((f) => {
        const value = values[f.key];

        // Booleans read as a statement with a checkbox, not a labelled input — these are the
        // "Receive candidate applications by email" style switches, and their help text is the
        // substance rather than a footnote.
        if (f.type === "boolean") {
          return (
            <label key={f.key} className="t1-switch t1s-cf-check">
              <input
                type="checkbox"
                role="switch"
                checked={value === true}
                disabled={disabled}
                onChange={(e) => onChange(f.key, e.target.checked)}
              />
              <span className="track" aria-hidden="true" />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: "var(--ink)" }}>{f.label}</span>
                {f.help && <div style={{ ...helpStyle, fontWeight: 400 }}>{f.help}</div>}
              </span>
            </label>
          );
        }

        const savedSecret = f.secret && secretsSet.includes(f.key);

        return (
          <div key={f.key}>
            <label style={labelStyle} htmlFor={`cf-${f.key}`}>
              {f.label}
              {f.required && <span style={{ color: "var(--crit)" }} aria-hidden="true">*</span>}
              {savedSecret && (
                <span className="pill v" style={{ marginLeft: 4 }}>
                  saved
                </span>
              )}
            </label>

            {f.type === "select" ? (
              <select
                id={`cf-${f.key}`}
                className="input"
                value={typeof value === "string" ? value : ""}
                disabled={disabled}
                onChange={(e) => onChange(f.key, e.target.value)}
                style={{ width: "100%" }}
              >
                <option value="">Select…</option>
                {(f.options ?? []).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : f.type === "textarea" ? (
              <textarea
                id={`cf-${f.key}`}
                className="input"
                rows={3}
                value={typeof value === "string" ? value : ""}
                placeholder={f.placeholder}
                maxLength={f.maxLength}
                disabled={disabled}
                onChange={(e) => onChange(f.key, e.target.value)}
                style={{ width: "100%" }}
              />
            ) : (
              <input
                id={`cf-${f.key}`}
                className="input"
                type={f.type === "password" ? "password" : f.type === "number" ? "number" : "text"}
                value={value === null || value === undefined ? "" : String(value)}
                // A saved credential is never echoed, so the box is empty — say what leaving it
                // empty will do rather than letting it look like the credential was lost.
                placeholder={savedSecret ? "•••••••• — leave blank to keep the saved value" : f.placeholder}
                min={f.min}
                max={f.max}
                maxLength={f.maxLength}
                disabled={disabled}
                autoComplete={f.secret ? "new-password" : "off"}
                onChange={(e) =>
                  onChange(f.key, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)
                }
                style={{ width: "100%" }}
              />
            )}

            {f.help && <div style={helpStyle}>{f.help}</div>}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The banner shown above the fields of a type the CUSTOMER must hold an account for.
 *
 * Stated plainly and up front, because the alternative — letting someone fill in a LinkedIn or
 * Naukri form and discover at test time that they needed a subscription we cannot sell them — is
 * how an integrations page loses trust.
 */
export function ProvisioningNote({
  provisioning,
  prerequisite,
  wired,
  label,
}: {
  provisioning: "native" | "external_subscription";
  prerequisite?: string | null;
  wired: boolean;
  label: string;
}): ReactNode {
  if (provisioning === "native" && wired) return null;
  return (
    <div className="banner info" style={{ marginBottom: 16 }}>
      <Info />
      <div style={{ fontSize: 13, lineHeight: 1.5 }}>
        {provisioning === "external_subscription" && (
          <div>
            <b>Needs your own account.</b> {prerequisite}
          </div>
        )}
        {!wired && (
          <div style={{ marginTop: provisioning === "external_subscription" ? 6 : 0 }}>
            <b>Not live yet.</b> {label} settings are saved, but the connection itself isn't
            implemented — this connector will stay inactive until it ships. Nothing is sent or
            received in the meantime.
          </div>
        )}
      </div>
    </div>
  );
}
